<template>
  <div v-if="!isMounted" :class="cn('flex justify-center', classComputed)">
    <AppLoaderLogo class="size-8" />
  </div>
  <div v-else :id="templateIdMap" :class="classComputed" />
</template>

<script setup lang="ts">
import { useMounted } from '@vueuse/core'

import type { Map, LatLng } from 'leaflet'
import type { DeepReadonly, HtmlHTMLAttributes } from 'vue'

import { cn } from '@/utils/shadcn'

import markerIcon from '~/assets/icons/location-on.svg?raw'

export type AppMapEvent = {
  addressByAddressId?: {
    location?: {
      latitude: number
      longitude: number
    } | null
  } | null
}
export type AppMapProps = {
  events?: readonly DeepReadonly<AppMapEvent>[]
  geocoder?: boolean
  positionInitial?: {
    latitude: number
    longitude: number
    zoomLevel: number
  }
}
const {
  class: classProps = undefined,
  events = undefined,
  geocoder = undefined,
  positionInitial = {
    latitude: 51.12421275782688,
    longitude: 10.283203125000002,
    zoomLevel: 6,
  },
} = defineProps<AppMapProps & { class?: HtmlHTMLAttributes['class'] }>()
const emit = defineEmits<{
  geocode: [name: string]
}>()
const classComputed = computed(() =>
  cn('h-96 w-full text-[#191911]', classProps),
)

const templateIdMap = useId()
const map = ref<Map>()
const mapCenter = ref<LatLng>()
const mapZoom = ref<number>()

const isMounted = useMounted()
onMounted(async () => {
  const L = await import('leaflet')
  map.value = L.map(templateIdMap, {
    maxBounds: [
      [-90, -180],
      [90, 180],
    ],
  }).setView(
    [positionInitial.latitude, positionInitial.longitude],
    positionInitial.zoomLevel,
  ) // Kassel: 51.3144643, 9.4957866
  L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution:
      '<a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    referrerPolicy: 'strict-origin-when-cross-origin', // TODO: remove when open street map is auto-detected in leaflet (https://github.com/Leaflet/Leaflet/pull/9883)
  }).addTo(map.value)

  if (geocoder) {
    const { Geocoder } = await import('leaflet-control-geocoder')
    new Geocoder({
      defaultMarkGeocode: false,
      // Nominatim's usage policy forbids autocomplete, so its `suggest()` call always rejects, and the control never clears its loading state when that happens, leaving the search permanently stuck.
      // Suggest-as-you-type is disabled here so users submit a search via Enter or the search icon instead, which use the working, non-suggest code path.
      suggestMinLength: Infinity,
    })
      .on('markgeocode', (markGeocodeEvent) => {
        map.value?.setView(markGeocodeEvent.geocode.center, 13)
        emit('geocode', markGeocodeEvent.geocode.name)
      })
      .addTo(map.value)
  }

  mapCenter.value = map.value.getCenter()
  mapZoom.value = map.value.getZoom()
  map.value.on('move', () => {
    mapCenter.value = map.value?.getCenter()
  })
  map.value.on('zoomend', () => {
    mapZoom.value = map.value?.getZoom()
  })

  if (events && events.length) {
    const icon = L.divIcon({
      className: 'text-(--critic-red-middle)',
      html: markerIcon,
      iconAnchor: [15, 25],
      iconSize: [30, 30],
      popupAnchor: [15, 2],
    })

    for (const event of events) {
      const location = event.addressByAddressId?.location
      if (!location) continue
      L.marker([location.latitude, location.longitude], { icon }).addTo(
        map.value,
      )
    }
  }
})
defineExpose({ map, mapCenter, mapZoom })
</script>

<style>
@import url('leaflet/dist/leaflet.css');
@import url('leaflet-control-geocoder/Control.Geocoder.css');
</style>
